import React, { useEffect, useState } from 'react';
import TodayReceptionV from './TodayReception.v';
import ReceptionModal from '../_common/modal/ReceptionModal';
import PageUtil from 'utils/Page.util';
import moment from 'moment';
import { withRouter } from 'core/withRouter';
import { dateFormat } from 'utils/Date.util';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import receptionService from 'services/reception.service';
import { DateType, getReceptionStatus, getReceptionType, ReceptionItemKind, ReceptionStatus } from 'enums/reception.enum';

/** 내부 상태 모델 */
class Params {
    error: string | null = null;
    isLoaded = false;
    total: number = 0;

    list: ReceptionDTO[] = [];
    search: ReceptionSearchDTO = new ReceptionSearchDTO();

    statusSet = getReceptionStatus();
    typeSet = ['일반', '맞교환', '출장', '보상판매', '방문', '판매'];
}

function TodayReception() {
    const [params, setParams] = useState(new Params());
    const [search, setSearch] = useState(false);

    // 모달 상태 관리 추가
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingRow, setEditingRow] = useState<ReceptionDTO | null>(null);

    /** 초기 진입 시 기본 검색기간 조회 */
    useEffect(() => {
        onTodaySearch();
    }, []);

    /** 검색 트리거 */
    useEffect(() => {
        if (!search) return;
        setSearch(false);
        getList(params.search);
    }, [search]);

    /** 목록 조회 */
    async function getList(search: ReceptionSearchDTO) {
        try {
            const res = await receptionService.list(search);

            setParams((prev) => ({
                ...prev,
                list: res.content || [],
                total: res.totalElements || 0,
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    /** 검색 버튼 */
    function onSearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로
        setParams(cur);
        setSearch(true);
    }

    /** Today 버튼 - 당일 데이터만 조회 */
    function onTodaySearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        const today = dateFormat(new Date(), 'YYYY-MM-DD');

        cur.search.dateType = DateType.RECEIVED;
        cur.search.firstStatus = ReceptionStatus.CHECK;
        cur.search.startDate = today;
        cur.search.endDate = today;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로

        setParams(cur);
        setSearch(true);
    }

    /** 행 상태 변경 → 즉시 저장(간단 예시) */
    async function onChangeStatus(row: ReceptionDTO, newStatus: ReceptionStatus) {
        if (!confirm("처리상황을 변경하시겠습니까?")) return;
        try {
            await receptionService.updateStatus({ id: row.id, status: newStatus });
            onSearch();
        } catch (error) {
            PageUtil.handleApiError(error);
        }
    }

    /** 수정 모달 열기 */
    function onOpenEdit(row: ReceptionDTO) {
        setModalMode('edit');
        setEditingRow(row);
        setShowModal(true);
    }

    /** 모달에서 저장 완료 시 */
    async function onModalSave(reception: ReceptionDTO, close: boolean = true) {
        try {
            console.log("reception", reception);
            await receptionService.save(reception);
            if (close) setShowModal(false);
            setSearch(true); // 목록 새로고침
            alert('저장되었습니다.');
            // 필요시 목록 새로고침 등 추가 처리
        } catch (error) {
            console.error('접수 저장 실패:', error);
            alert('저장에 실패했습니다.');
        }
    }

    /** 모달 닫기 */
    function onModalClose() {
        setShowModal(false);
        setEditingRow(null);
    }

    const handleExportExcel = async () => {

        const cur = PageUtil.deepCopy(params) as Params;
        const search = cur.search;
        const today = dateFormat(new Date(), 'YYYY-MM-DD');
        search.dateType = DateType.RECEIVED;
        search.startDate = today;
        search.endDate = today;
        search.rows = 9999;
        search.page = 1;

        const res = await receptionService.list(search);

        try {
            const excelData = res.content.map((row) => {

                const boilerItem = row.items?.find(p => p.kind === ReceptionItemKind.BOILER);

                return {
                    '처리상황': getReceptionStatus().find(s => s.value === row.status)?.label || row.status || '',
                    '처리유형': getReceptionType().find((t) => t.value === row.type)?.label || row.type || '',
                    '접수일자': row.receivedDate || '',
                    '연락처': row.phone1 || '',
                    '고객명': row.customerName || '',
                    '도': row.province || '',
                    '지역': row.city || '',
                    // '보일러명': row.items?.filter(p => p.kind === ReceptionItemKind.BOILER).map(p => p.options?.map(o => o.productName).join(', ')).join(', ') || '',
                    '보일러명': boilerItem?.modelName || '',
                    '제조년월': row.items?.filter(p => p.manufactureMonth).map(p => p.manufactureMonth).join(', ') || '',
                    '특이사항': row.items?.filter(p => p.note).map(p => p.note).join(', ') || '',
                    '접수내용': row.requestNote || '',
                    '처리내용': row.handleNote || '',
                }
            });

            const today = PageUtil.getCurrentDate();
            let filename = `TODAY접수_${today}`;

            filename += '.xlsx';

            PageUtil.exportToExcel(excelData, filename, 'TODAY접수');

        } catch (error) {
            console.error('엑셀 내보내기 오류:', error);
            alert('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };

    const args = {
        params,
        setParams,
        setSearch,
        onOpenEdit,
        onChangeStatus,
        handleExportExcel
    };

    return (
        <>
            <TodayReceptionV {...args} />

            {/* 접수 등록/수정 모달 */}
            <ReceptionModal
                show={showModal}
                title={modalMode === 'create' ? '신규 접수 등록' : '접수 수정'}
                initial={editingRow}
                onSave={onModalSave}
                onClose={onModalClose}
            />
        </>
    );
}

export default withRouter(TodayReception);